import React, { useMemo, useEffect, useState, useCallback } from "react";
import type { ColDef, IDatasource, IGetRowsParams, IServerSideDatasource } from "ag-grid-community";
import type { RowSelectionOptions } from "ag-grid-community";
import type { GridApi } from "ag-grid-community";
import axios from "axios";
import useAppData from "@/store/useAppData";
import { RouterStoreItemKey } from "@/store/useRouter/types";
import useRouter from "@/store/useRouter";
import { useShallow } from "zustand/shallow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faClock, faWarehouse, faListCheck, faShirt } from "@fortawesome/free-solid-svg-icons";
import type { FloatingAction } from "../FloatingActions";
import type { Item, FilterKind, Stock } from "./types";
import type { ItemListOdmrazProps } from "./itemListOdmraz";
import { DeicingItem } from "../DeicingList/types";
import { ExcelColumn, exportItemsToExcel } from "@/Utils/exportInventoryResultToExcel";
import { exportDeicingProtocolPdf } from "@/Utils/exportDeiceToPdf";
import logoAirlines from "../Login/logoAirlines.png";
import { urlToDataUrl } from "@/Utils/exportInventoryResultToPdf";
import { DeicingSummaryFilterData } from "../DeicingSummaryFilterDialogWindow";
import { DeicingLogFilterData } from "../DeicingEcoFilterDialogWindow";

function mapFilter(filter?: FilterKind): ColDef["filter"] {
  if (filter === "text") return "agTextColumnFilter";
  if (filter === "number") return "agNumberColumnFilter";
  if (filter === "set") return "agSetColumnFilter";
  return false;
}

type DeicingGridRow =
  | {
    id: string;
    rowType: "dateGroup";
    date: string;
    level: 0;
  }
  | {
    id: string;
    rowType: "flightGroup";
    date: string;
    flightNumber: string;
    level: 1;
  }
  | (DeicingItem & {
    rowType: "item";
    level: 2;
  });

export type DeicingGridRowSummary = {
  id?: string;
  date?: string | null;
  type1Start?: number | null;
  type1End?: number | null;
  type2Start?: number | null;
  type2End?: number | null;
  notes?: string | null;
  type1FluidConsumption?: number | null;
  type2FluidConsumption?: number | null;
  waterConsumption?: number | null;
  fluid1Ratio?: number | null;
  fluid2Ratio?: number | null;
  deice1?: string | null;
  deice2?: string | null;
  deice3?: string | null;
  operator1?: string | null;
  operator2?: string | null;
  operator3?: string | null;
  checkedBy?: string | null;
  stand?: string | null;
  standName?: string | null;
  company?: number | null;
  aircraftType?: string;
  aircraftRegistration?: string;
  flightNumber?: string;
  oat?: number | null;
  wingTemperature?: number | null;
  weatherCode?: string;
  weatherName?: string;
  weatherEngName?: string;
  companyName?: string | null;
  most?: boolean;
  deicingDuration?: number | null;
  checkedDate?: string | null;
  checked?: boolean;
  nextOperator1?: string | null;
  nextOperator2?: string | null;
  nextOperator3?: string | null;
}

export type DeicingExcelRow = {
  rok: number;
  mesic: number;
  cisloTydne: number;
  tyden: string;
  stani: string;
  ok1: number;
  ok2: number;
  voda: number;
};

const useItemListLogicOdmraz = ({
  columns,
  fetchItems,
  scope,
  expanded,
  toggleNode,
}: ItemListOdmrazProps) => {

  const setSelectedStock = useAppData(state => state.setSelectedStock);
  const selectedStock = useAppData(state => state.selectedStock);
  const selectedModule = useAppData(state => state.selectedModule);
  const loggedUser = useAppData(state => state.user);
  const setSelectedDeicing = useAppData(state => state.setSelectedDeicing);
  const gridApiRef = React.useRef<GridApi<Item> | null>(null);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);

  const STANDS = [
    { label: "APN EAST", value: "DPNE" },
    { label: "APN SOUTH", value: "DPNS" },
    { label: "DE-ICING AREA 1", value: "D1" },
    { label: "DE-ICING AREA 2", value: "D2" },
    { label: "DE-ICING AREA 3", value: "D3" },
    { label: "DE-ICING AREA 4", value: "D4" },
    { label: "DE-ICING AREA 5", value: "D5" },
    { label: "DE-ICING AREA 6", value: "D6" },
    { label: "TWY J", value: "TWY J" }
  ] as const;

  const router = useRouter(
    useShallow(state => ({
      go: state.go,
      history: state.history
    }))
  );

  // const rowSelection: RowSelectionOptions<Item> = {
  //   mode: "singleRow",
  //   checkboxes: true,
  //   //headerCheckbox: true,
  // };

  const rowSelection: RowSelectionOptions<Item> = {
    mode: "singleRow",
    checkboxes: false,
    enableClickSelection: true
  };



  const onRowClicked = (event: any) => {
    const row = event.data;

    if (!row) {
      return;
    }

    if (
      row.type === "date" ||
      row.type === "flight"
    ) {
      toggleNode && toggleNode(row.treeId);
    }
  }

  // const dateFormat = (date: string, dateOnly?: boolean) => {
  //   const d = new Date(date);

  //   const pad = (n: number) => String(n).padStart(2, "0");

  //   if (dateOnly) {
  //     return (
  //       pad(d.getDate()) + "." +
  //       pad(d.getMonth() + 1) + "." +
  //       d.getFullYear()
  //     );
  //   } else {
  //     return (
  //       pad(d.getDate()) + "." +
  //       pad(d.getMonth() + 1) + "." +
  //       d.getFullYear() + " " +
  //       pad(d.getHours()) + ":" +
  //       pad(d.getMinutes()) + ":" +
  //       pad(d.getSeconds())
  //     );
  //   }
  // };

  const actions: FloatingAction[] = [
    // ...(loggedUser?.userrole != "viewer" ? [{
    //   key: "2",
    //   label: "Nový odmraz",
    //   icon: <FontAwesomeIcon icon={faWarehouse} />,
    //   onClick: () => { 
    //     setSelectedDeicing(null);
    //     router.go({ key: RouterStoreItemKey.DEICINGCARD }); 
    //   }
    // }] : []),
    // {
    //   key: "5",
    //   label: "Odmrazy",
    //   icon: <FontAwesomeIcon icon={faWarehouse} />,
    //   onClick: () => router.go({ key: RouterStoreItemKey.DEICINGLIST })
    // },
    ...(loggedUser?.userrole != "viewer" ? [{
      key: "2",
      label: "Příjem na sklad",
      icon: <FontAwesomeIcon icon={faWarehouse} />,
      onClick: () => router.go({ key: RouterStoreItemKey.STOCKINGDEICINGCARD })
    }] : []),
    ...(loggedUser?.userrole != "viewer" ? [{
      key: "3",
      label: "Technologický odmraz",
      icon: <FontAwesomeIcon icon={faListCheck} />,
      onClick: () => router.go({ key: RouterStoreItemKey.DEICINGCARDTECH })
    }] : []),
    ...(loggedUser?.userrole != "viewer" ? [{
      key: "3",
      label: "Inventura",
      icon: <FontAwesomeIcon icon={faListCheck} />,
      onClick: () => router.go({ key: RouterStoreItemKey.INVENTORYDEICE })
    }] : []),
    {
      key: "4",
      label: "Skladové pohyby",
      icon: <FontAwesomeIcon icon={faClock} />,
      onClick: () => router.go({ key: RouterStoreItemKey.HISTORYITEMSODMRAZ })
    }
  ];

  const [selectedStockState, setSelectedStockState] = React.useState<string>();
  const [stocksState, setStocksState] = React.useState<Stock[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [countsByState, setCountsByState] = useState<Array<{ key: string; count: number }>>([]);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [rowData, setRowData] = useState<any[]>([]);

  useEffect(() => {
    loadStocks();
  }, [selectedModule]);

  useEffect(() => {
    const load = async () => {
      if (!selectedStockState || !selectedStock) {
        setRowData([]);
        setTotalCount(0);
        setCountsByState([]);
        return;
      }

      try {
        const result = await fetchItems({
          startRow: 0,
          endRow: 1000000,
          agParams: {
            stockId: selectedStockState,
          },
        });

        setTotalCount(result.totalQuantity ?? 0);
        setCountsByState(result.countsByState ?? []);

        if (scope === "all") {
          setRowData(result.rows);
        } else if (scope === "allItemsGrouped") {
          setRowData(createGroupedRows(result.rows));
        } else {

          let data = formatDeicingDataToSummaryData(result.rows);

          data = data.filter(item => item?.aircraftRegistration && item?.flightNumber);

          setRowData(data);
        }
      } catch (error) {
        console.error(error);
        setRowData([]);
      }
    };

    load();
  }, [
    scope,
    fetchItems,
    selectedStock,
    selectedStockState,
    refresh,
    expanded
  ]);

  const onPrintEcoProtocol = (filter?: DeicingLogFilterData) => {
    const deicingExcelColumns: ExcelColumn<DeicingExcelRow>[] = [
      {
        header: "mesic",
        field: "mesic",
        type: "number",
        width: 10,
        align: "center"
      },
      {
        header: "cislo_tydne",
        field: "cisloTydne",
        type: "number",
        width: 10,
        align: "center"
      },
      {
        header: "tyden",
        field: "tyden",
        type: "text",
        width: 22,
        align: "center"
      },
      {
        header: "stani",
        field: "stani",
        type: "text",
        width: 18,
      },
      {
        header: "OK I",
        field: "ok1",
        type: "number",
        width: 10,
        align: "center"
      },
      {
        header: "OK II",
        field: "ok2",
        type: "number",
        width: 10,
        align: "center"
      },
      {
        header: "voda",
        field: "voda",
        type: "number",
        width: 10,
        align: "center"
      }
    ];

    const seasonStartYear = getSeasonStartYear(filter?.year ? new Date(filter.year, 0, 1) : new Date());

    const excelData = createDeicingExcelData(
      rowData,
      seasonStartYear
    );

    exportItemsToExcel<DeicingExcelRow>(
      excelData,
      deicingExcelColumns,
      {
        sheetName: "Ekologie",
        fileName: "Ekologie.xlsx",
        title: "Výkaz pro ekologii",
      }
    );
  }

  const createDeicingExcelData = (
    data: DeicingGridRowSummary[],
    seasonStartYear: number
  ): DeicingExcelRow[] => {
    const result: DeicingExcelRow[] = [];

    // září -> květen
    const months = [
      { year: seasonStartYear, month: 9 },
      { year: seasonStartYear, month: 10 },
      { year: seasonStartYear, month: 11 },
      { year: seasonStartYear, month: 12 },

      { year: seasonStartYear + 1, month: 1 },
      { year: seasonStartYear + 1, month: 2 },
      { year: seasonStartYear + 1, month: 3 },
      { year: seasonStartYear + 1, month: 4 },
      { year: seasonStartYear + 1, month: 5 },
    ];

    for (const { year, month } of months) {
      // Vygeneruje VŠECHNY týdny daného měsíce
      const weeks = getWeeksInMonth(year, month);

      for (const week of weeks) {
        // Pro každý týden VŠECHNA stání
        for (const stand of STANDS) {
          const items = data.filter(item => {
            if (!item.date) {
              return false;
            }

            const date = new Date(item.date);

            return (
              date >= week.from &&
              date <= week.to &&
              item.stand === stand.value
            );
          });

          result.push({
            rok: year,
            mesic: month,
            cisloTydne: week.weekNumber,
            tyden: `${formatDate(week.from)} - ${formatDate(week.to)}`,
            stani: stand.label,
            ok1: items.reduce(
              (sum, item) => sum + (item.type1FluidConsumption ?? 0),
              0
            ),
            ok2: items.reduce(
              (sum, item) => sum + (item.type2FluidConsumption ?? 0),
              0
            ),
            voda: items.reduce(
              (sum, item) => sum + (item.waterConsumption ?? 0),
              0
            ),
          });
        }
      }
    }

    return result;
  };

  const getSeasonStartYear = (date = new Date()): number => {
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    // leden-květen patří do sezóny začínající předchozí rok
    // if (month >= 1 && month <= 5) {
    //   return year - 1;
    // }

    return year;
  };

  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  type MonthWeek = {
    weekNumber: number;
    from: Date;
    to: Date;
  };

  const getWeeksInMonth = (
    year: number,
    month: number
  ): MonthWeek[] => {
    const weeks: MonthWeek[] = [];

    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0);

    let current = new Date(monthStart);

    while (current <= monthEnd) {
      const dayOfWeek = current.getDay() || 7;

      // pondělí
      const monday = new Date(current);
      monday.setDate(current.getDate() - dayOfWeek + 1);
      monday.setHours(0, 0, 0, 0);

      // neděle
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);

      // ořízneme týden podle měsíce
      const from =
        monday < monthStart
          ? new Date(monthStart)
          : monday;

      const to =
        sunday > monthEnd
          ? new Date(monthEnd)
          : sunday;

      to.setHours(23, 59, 59, 999);

      weeks.push({
        weekNumber: getISOWeekNumber(current),
        from,
        to,
      });

      // posun na další týden
      current = new Date(to);
      current.setDate(current.getDate() + 1);
      current.setHours(0, 0, 0, 0);
    }

    return weeks;
  };

  const getISOWeekNumber = (date: Date): number => {
    const d = new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      )
    );

    const dayNum = d.getUTCDay() || 7;

    d.setUTCDate(d.getUTCDate() + 4 - dayNum);

    const yearStart = new Date(
      Date.UTC(d.getUTCFullYear(), 0, 1)
    );

    return Math.ceil(
      (((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7
    );
  };

  const onPrintProtocol = async () => {

    const rows = gridApiRef.current?.getSelectedRows() ?? [];

    if (rows.length > 0) {

      const selectedItem: DeicingGridRowSummary = rows[0];

      const now = new Date();

      const dateFileName =
        now.getFullYear() +
        String(now.getMonth() + 1).padStart(2, "0") +
        String(now.getDate()).padStart(2, "0");

      const logoBase64 = await urlToDataUrl(logoAirlines);
      exportDeicingProtocolPdf(selectedItem, {
        fileName: `${dateFileName}-${selectedItem.aircraftRegistration}.pdf`,
        logoDataUrl: logoBase64
      });
    }
  }

  const dateFormat = (
    date?: string | number | null,
    dateOnly?: boolean,
    timeOnly?: boolean
  ) => {
    if (date == null) return "";

    const pad = (n: number) => String(n).padStart(2, "0");

    // number = počet minut
    if (timeOnly) {
      const hours = Math.floor(Number(date) / 60);
      const minutes = Number(date) % 60;

      return `${pad(hours)}:${pad(minutes)}`;
    }

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) return "";

    if (dateOnly) {
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
    }

    return (
      `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ` +
      `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    );
  };

  const dateWithoutTimezoneShift = (
    value?: number | string | null
  ): string | null => {
    if (value == null) {
      return null;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds()
    ).toISOString();
  };

  const onPrintSummaryItems = (filter?: DeicingSummaryFilterData) => {
    const deicingExcelColumns: ExcelColumn<DeicingGridRowSummary>[] = [
      {
        header: "Datum",
        field: "date",
        type: "date",
        width: 13,
        align: "center",
        getValue: row => {
          if (row.date == null) return "";

          const date = new Date(row.date);

          const pad = (n: number) => String(n).padStart(2, "0");

          return `${pad(date.getUTCDate())}.${pad(date.getUTCMonth() + 1)}.${date.getUTCFullYear()}`;
        }
      },
      {
        header: "Začátek",
        getValue: row =>
          row.type1Start != null
            ? dateWithoutTimezoneShift(row.type1Start)
            : null,
        type: "date",
        width: 18,
        align: "center",
      },
      {
        header: "Konec",
        getValue: row =>
          row.type1End != null
            ? dateWithoutTimezoneShift(row.type1End)
            : null,
        type: "date",
        width: 18,
        align: "center",
      },
      {
        field: "deicingDuration",
        header: "Čas odmrazení",
        width: 15,
        align: "center",
        getValue: row => {
          if (!row.deicingDuration) return "";

          return dateFormat(row.deicingDuration, false, true);
        }
      },
      {
        header: "Stojánka",
        field: "stand",
        type: "number",
        width: 12,
        align: "center",
      },
      {
        header: "Typ letadla",
        field: "aircraftType",
        width: 16,
      },
      {
        header: "Imatrikulace",
        field: "aircraftRegistration",
        width: 16,
      },
      {
        header: "Číslo letu",
        field: "flightNumber",
        width: 13,
      },
      {
        header: "Společnost",
        field: "companyName",
        type: "number",
        width: 18,
        align: "center",
      },
      {
        header: "Spotřeba kap. typ I",
        field: "type1FluidConsumption",
        type: "number",
        width: 19,
        align: "right",
      },
      {
        field: "fluid1Ratio",
        header: "Poměr kap. v %",
        type: "number",
        // flex: 0.8,
        width: 16,
        align: "right",
      },
      {
        header: "Spotřeba kap. typ II",
        field: "type2FluidConsumption",
        type: "number",
        width: 19,
        align: "right",
      },
      {
        header: "Spotřeba vody",
        field: "waterConsumption",
        type: "number",
        width: 16,
        align: "right",
      },
      {
        header: "OAT",
        field: "oat",
        type: "number",
        width: 10,
        align: "right",
      },
      {
        header: "Teplota křídel",
        field: "wingTemperature",
        type: "number",
        width: 15,
        align: "right",
      },
      {
        header: "Počasí",
        field: "weatherName",
        width: 14,
        align: "center",
      },
      {
        field: "notes",
        header: "Poznámky",
        width: 20
      },
      {
        field: "most",
        header: "Most",
        width: 8,
        // flex: 1,
        // filter: "text",
        // floatingFilter: true,
        getValue: row => {
          return !!row.most ? "Y" : "N";
        }
      },
      {
        header: "Vozidlo 1",
        field: "deice1",
        width: 17,
      },
      {
        header: "Vozidlo 2",
        field: "deice2",
        width: 17,
      },
      {
        header: "Vozidlo 3",
        field: "deice3",
        width: 17,
      },
      {
        header: "Sluchátka",
        field: "checkedBy",
        width: 17,
      },
      {
        header: "Pracovník 1",
        field: "operator1",
        width: 17,
        getValue: row => {
          return row?.nextOperator1 && row?.operator1 ? `${row.operator1}, ${row.nextOperator1}` : row?.operator1 ?? row.nextOperator1;
        }
      },
      {
        header: "Pracovník 2",
        field: "operator2",
        width: 17,
        getValue: row => {
          return row?.nextOperator2 && row?.operator2 ? `${row.operator2}, ${row.nextOperator2}` : row?.operator2 ?? row.nextOperator2;
        }
      },
      {
        header: "Pracovník 3",
        field: "operator3",
        width: 17,
        getValue: row => {
          return row?.nextOperator3 && row?.operator3 ? `${row.operator3}, ${row.nextOperator3}` : row?.operator3 ?? row.nextOperator3;
        }
      }
    ];

    const filteredData = rowData?.filter(item => {

      if ((filter?.dateFrom || filter?.dateTo) && !item.date) {
        return false;
      }

      if (
        filter?.company &&
        item.company !== Number(filter.company)
      ) {
        return false;
      }

      if (item.date) {
        const itemDate = new Date(item.date);

        if (filter?.dateFrom) {
          const dateFrom = new Date(filter.dateFrom);
          dateFrom.setHours(0, 0, 0, 0);

          if (itemDate < dateFrom) {
            return false;
          }
        }

        if (filter?.dateTo) {
          const dateTo = new Date(filter.dateTo);
          dateTo.setHours(23, 59, 59, 999);

          if (itemDate > dateTo) {
            return false;
          }
        }
      }

      return true;
    });

    exportItemsToExcel(
      filteredData,
      deicingExcelColumns,
      {
        sheetName: "Odmrazování",
        fileName: "odmrazovani.xlsx",
        title: "Přehled odmrazování",
      }
    );
  }

  //   const treeRows = useMemo<TreeRow[]>(() => {
  //     
  // }, [items, expanded]);

  const createGroupedRows = (
    rows: DeicingItem[]
  ): DeicingGridRow[] => {
    const result: any[] = [];

    const groupedByDate = new Map<
      string,
      Map<string, DeicingItem[]>
    >();

    rows.forEach(item => {
      const date = item?.date ? dateFormat(item?.date, true) : "---";

      if (!groupedByDate.has(date)) {
        groupedByDate.set(date, new Map());
      }

      const flights = groupedByDate.get(date)!;

      const flightNumber = item?.flightNumber ?? "---";

      if (!flights.has(flightNumber)) {
        flights.set(flightNumber, []);
      }

      flights.get(flightNumber)!.push(item);
    });

    groupedByDate.forEach((flights, date) => {
      const dateId = `date-${date}`;

      result.push({
        treeId: dateId,
        type: "date",
        level: 0,
        label: date,
        date
      });

      if (!expanded?.has(dateId)) {
        return;
      }

      flights.forEach((flightItems, flightNumber) => {
        const flightId = `${dateId}-flight-${flightNumber}`;

        result.push({
          treeId: flightId,
          type: "flight",
          level: 1,
          label: flightNumber,
          date,
          flightNumber
        });

        if (!expanded.has(flightId)) {
          return;
        }

        flightItems.forEach(item => {
          result.push({
            treeId: `item-${item.id}`,
            type: "item",
            level: 2,
            label: item.stockName,
            data: item,
            ...item
            // date: item.date,
            // flightNumber: item.flightNumber,
            // stockName: item.stockName,

            // type1Start: item.type1Start,
            // type1End: item.type1End
          });
        });
      });
    });

    return result;
  };

  const formatDeicingDataToSummaryData = (
    rows: DeicingItem[]
  ): DeicingGridRowSummary[] => {
    const result: DeicingGridRowSummary[] = [];

    const groupedByFlight = new Map<
      string,
      Map<string, DeicingItem[]>
    >();

    rows.forEach(item => {

      const flightNumber = item?.aircraftRegistration && item?.flightNumber ? `${item?.aircraftRegistration}-${item?.flightNumber}` : "---";

      if (!groupedByFlight.has(flightNumber)) {
        groupedByFlight.set(flightNumber, new Map());
      }

      const deicings = groupedByFlight.get(flightNumber)!;

      const deicingId = item?.id ?? "---";

      if (!deicings.has(deicingId)) {
        deicings.set(deicingId, []);
      }

      deicings.get(deicingId)!.push(item);
    });

    groupedByFlight.forEach(flight => {

      const resultItem: DeicingGridRowSummary = {};

      //const firstDeicingItem = flight.entries[0][0] as DeicingItem;
      const firstDeicingItem = flight.values().next().value?.[0];

      resultItem.stand = firstDeicingItem?.standName;
      resultItem.company = firstDeicingItem?.company;
      resultItem.aircraftType = firstDeicingItem?.aircraftType;
      resultItem.aircraftRegistration = firstDeicingItem?.aircraftRegistration;
      resultItem.flightNumber = firstDeicingItem?.flightNumber;
      // resultItem.oat = firstDeicingItem?.oat;
      // resultItem.wingTemperature = firstDeicingItem?.wingTemperature;
      resultItem.weatherName = firstDeicingItem?.weatherName;
      resultItem.weatherEngName = firstDeicingItem?.weatherEngName;
      resultItem.weatherCode = firstDeicingItem?.weatherCode;
      resultItem.companyName = firstDeicingItem?.companyName;
      resultItem.checkedBy = firstDeicingItem?.checkedByName;
      resultItem.date = firstDeicingItem?.date;
      resultItem.most = flight.size > 1;
      resultItem.checkedDate = firstDeicingItem?.checkedDate;

      let index = 0;
      let allNotes = "";
      let type2FluidConsumption = 0;
      let firstStart: number | null = null;
      let lastEnd: number | null = null;
      let lowestWingTemperature: number | null = null;
      let lowestOat: number | null = null;
      flight.forEach((deice) => {

        if (index === 0) {
          resultItem.deice1 = deice[0]?.stockName;
          resultItem.operator1 = deice[0]?.operator1;

          if (deice[0]?.nextOperatorName) {
            resultItem.nextOperator1 = deice[0]?.nextOperatorName;
          }
        }

        if (index === 1) {
          resultItem.deice2 = deice[0]?.stockName;
          resultItem.operator2 = deice[0]?.operator1;

          if (deice[0]?.nextOperatorName) {
            resultItem.nextOperator2 = deice[0]?.nextOperatorName;
          }
        }

        if (index === 2) {
          resultItem.deice3 = deice[0]?.stockName;
          resultItem.operator3 = deice[0]?.operator1;

          if (deice[0]?.nextOperatorName) {
            resultItem.nextOperator3 = deice[0]?.nextOperatorName;
          }
        }

        index++;

        allNotes += deice[0]?.note && deice[0]?.note !== "" ? `${deice[0]?.note}|` : "";

        deice.forEach(deiceItem => {
          allNotes += deiceItem.itemNote && deiceItem?.itemNote !== "" ? `${deiceItem?.itemNote}|` : "";
        })

        type2FluidConsumption += Number(deice[0]?.type3FluidConsumption) || 0;

        lowestOat = deice[0]?.oat && lowestOat ? Math.min(deice[0]?.oat, lowestOat) : deice[0]?.oat ?? lowestOat;
        lowestWingTemperature = deice[0]?.wingTemperature && lowestWingTemperature ? Math.min(deice[0]?.wingTemperature, lowestWingTemperature) : deice[0]?.wingTemperature ?? lowestWingTemperature;

        const starts = [deice[0]?.type1Start, deice[0]?.type2Start]
          .filter(Boolean)
          .map(value => new Date(value!).getTime())
          .filter(Number.isFinite);

        if (starts?.length) {
          firstStart = firstStart ? Math.min(...starts, firstStart) : Math.min(...starts)
        }

        const ends = [deice[0]?.type1End, deice[0]?.type2End]
          .filter(Boolean)
          .map(value => new Date(value!).getTime())
          .filter(Number.isFinite);

        if (ends?.length) {
          lastEnd = lastEnd ? Math.max(...ends, lastEnd) : Math.max(...ends)
        }
      })

      resultItem.type1Start = firstStart;
      resultItem.type1End = lastEnd;
      resultItem.deicingDuration = !firstStart || !lastEnd ? null : Math.round(((lastEnd ?? 0) - (firstStart ?? 0)) / 60_000);
      resultItem.notes = allNotes;
      resultItem.type2FluidConsumption = type2FluidConsumption > 0 ? type2FluidConsumption : null;
      resultItem.fluid2Ratio = type2FluidConsumption > 0 ? 100 : null;
      resultItem.wingTemperature = lowestWingTemperature ?? lowestOat;
      resultItem.oat = lowestOat;

      let type1FluidConsumption = 0;
      let waterConsumption = 0;
      let type1FluidConsumptionMax = 0;
      let fluid1Ratio = 0;

      flight.forEach((deice) => {
        deice?.forEach(deiceItem => {
          type1FluidConsumption += Number(deiceItem.itemFluidConsumption) || 0;
          waterConsumption += Number(deiceItem.itemWaterConsumption) || 0;

          if (deiceItem?.itemFluidConsumption && Number(deiceItem?.itemFluidConsumption) > type1FluidConsumptionMax) {
            type1FluidConsumptionMax = Number(deiceItem?.itemFluidConsumption);
            fluid1Ratio = deiceItem?.itemFluidRatio && deiceItem?.itemFluidRatio != "" ? Number(deiceItem?.itemFluidRatio?.split("/")[0]) : 0;
          }
        })
      })

      resultItem.type1FluidConsumption = type1FluidConsumption > 0 ? type1FluidConsumption : null;
      resultItem.waterConsumption = waterConsumption > 0 ? waterConsumption : null;
      resultItem.fluid1Ratio = fluid1Ratio > 0 ? fluid1Ratio : null;

      result.push(resultItem);
    });

    return result;
  };

  // const createGroupedRows = (
  //   rows: DeicingItem[]
  // ): DeicingGridRow[] => {
  //   const result: DeicingGridRow[] = [];

  //   const sorted = [...rows].sort((a, b) => {
  //     const dateCompare =
  //       new Date(b.date).getTime() -
  //       new Date(a.date).getTime();

  //     if (dateCompare !== 0) {
  //       return dateCompare;
  //     }

  //     return (a.flightNumber ?? "").localeCompare(
  //       b.flightNumber ?? ""
  //     );
  //   });

  //   let currentDate: string | null = null;
  //   let currentFlightNumber: string | null = null;

  //   for (const item of sorted) {
  //     const date = item?.date?.substring(0, 10);

  //     if (date !== currentDate) {
  //       result.push({
  //         id: `date-${date}`,
  //         rowType: "dateGroup",
  //         date: date ?? "",
  //         level: 0,
  //       });

  //       currentDate = date ?? null;
  //       currentFlightNumber = null;
  //     }

  //     if (item.flightNumber !== currentFlightNumber) {
  //       result.push({
  //         id: `flight-${date}-${item?.flightNumber ?? ""}`,
  //         rowType: "flightGroup",
  //         date: date ?? "",
  //         flightNumber: item?.flightNumber ?? "",
  //         level: 1,
  //       });

  //       currentFlightNumber = item.flightNumber ?? null;
  //     }

  //     result.push({
  //       ...item,
  //       rowType: "item",
  //       level: 2,
  //     });
  //   }

  //   return result;
  // };

  const loadStocks = async () => {

    try {
      const res = await (
        axios({
          method: "post",
          url: "api/stock/getAll",
          data: {
            userId: loggedUser?.id,
            module: selectedModule?.id
          }
        })
      );

      if (!res?.data?.stocks?.length || res?.data?.stocks?.length === 0) {
        setSelectedStockState(undefined);
        setSelectedStock(null);

        setStocksState([]);

        return;
      }

      setStocksState(res.data.stocks);

      if (selectedStock && res.data.stocks.find(stock => stock.id === selectedStock.id)) {
        setSelectedStockState(selectedStock?.id);
      } else {
        setSelectedStockState(String(res.data.stocks[0].id))
        setSelectedStock(res.data.stocks[0]);
      }

    } catch (err) {
      throw err;
    }
  }

  const columnDefs = useMemo<ColDef<Item>[]>(() => {

    return columns.map((c) => ({
      field: c.field as any,
      headerName: c.headerName,
      flex: c.flex,
      filter: mapFilter(c.filter),
      floatingFilter: c.floatingFilter,
      valueFormatter: c?.valueFormatter,
      checkboxSelection: c?.checkboxSelection,
      headerCheckboxSelection: c?.headerCheckboxSelection,
      maxWidth: c?.maxWidth,
      editable: c?.editable,
      cellRenderer: c?.cellRenderer,
      minWidth: c?.minWidth
    }));
  }, [columns]);

  // const datasource = useMemo<IDatasource>(() => ({
  //   getRows: async (params: IGetRowsParams) => {

  //     try {

  //       if (!selectedStockState || !selectedStock) {

  //         setTotalCount(0);
  //         setCountsByState([]);

  //         params.successCallback([], 0);

  //         return;
  //       }

  //       const result = await fetchItems({
  //         startRow: params.startRow,
  //         endRow: params.endRow,
  //         agParams: { stockId: selectedStockState, ...params }
  //       });

  //       setTotalCount(result.totalQuantity ?? 0);
  //       setCountsByState(result.countsByState ?? []);

  //       params.successCallback(result.rows, result.totalCount);
  //     } catch {
  //       params.failCallback();
  //     }
  //   },
  // }), [fetchItems, selectedStock, selectedStockState, refresh]);

  const onChangeSelectedStock = (e) => {
    setSelectedStock(stocksState.filter(stock => stock.id == e.target.value)[0]);
    setSelectedStockState(e.target.value);

    gridApiRef?.current?.deselectAll();
    gridApiRef?.current?.refreshInfiniteCache();
  }

  return {
    setSelectedStock,
    selectedStock,
    loggedUser,
    gridApiRef,
    rowSelection,
    actions,
    selectedStockState,
    setSelectedStockState,
    stocksState,
    totalCount,
    countsByState,
    refresh,
    setRefresh,

    loadStocks,

    columnDefs,
    //datasource,

    onChangeSelectedStock,
    rowData,
    onRowClicked,
    onPrintSummaryItems,
    onPrintProtocol,
    onPrintEcoProtocol
  };
};

export default useItemListLogicOdmraz;